import { getApiBaseUrl } from './chatIdentity';

const API_BASE_URL = getApiBaseUrl();
const PARTNER_DASHBOARD_BASE = `${API_BASE_URL}/api/partner-dashboard`;

function partnerDashboardQuery(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.partner_shop_id) searchParams.set('partner_shop_id', String(params.partner_shop_id));
    if (params.status && params.status !== 'all') searchParams.set('status', String(params.status));
    if (params.q) searchParams.set('q', String(params.q));
    if (params.estimate_state && params.estimate_state !== 'all') {
        searchParams.set('estimate_state', String(params.estimate_state));
    }
    const query = searchParams.toString();
    return query ? `?${query}` : '';
}

function formatApiErrorDetail(detail) {
    if (!detail) return '';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                if (!item) return '';
                if (typeof item === 'string') return item;
                const path = Array.isArray(item.loc)
                    ? item.loc.filter((part) => part !== 'body').join('.')
                    : '';
                const message = item.msg || item.message || item.detail || '';
                return [path, message].filter(Boolean).join(': ');
            })
            .filter(Boolean)
            .join('; ');
    }
    if (typeof detail === 'object') {
        return detail.message || detail.msg || detail.detail || JSON.stringify(detail);
    }
    return String(detail);
}

function getApiErrorMessage(data, fallback = 'Partner shop request failed.') {
    return formatApiErrorDetail(data?.detail) || formatApiErrorDetail(data?.message) || fallback;
}

async function readJson(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(getApiErrorMessage(data));
        error.status = response.status;
        error.code = data?.detail?.code || null;
        error.data = data;
        throw error;
    }
    return data;
}

async function readJsonOrError(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(getApiErrorMessage(data));
        error.status = response.status;
        error.data = data;
        throw error;
    }
    return data;
}

// With a ZIP or browser coordinates, partners are returned inside the selected radius.
// Omitting location still returns every active shop for non-search callers.
export async function listPartnerShops({ zip = '', diagnosticTicketId = '', radiusMiles = '', coords = null } = {}) {
    const params = new URLSearchParams();
    if (zip) params.set('zip', zip);
    if (diagnosticTicketId) params.set('diagnostic_ticket_id', String(diagnosticTicketId));
    if (radiusMiles) params.set('radius_miles', String(radiusMiles));
    if (Number.isFinite(coords?.latitude) && Number.isFinite(coords?.longitude)) {
        params.set('latitude', String(coords.latitude));
        params.set('longitude', String(coords.longitude));
    }
    const nextQuery = params.toString();
    const response = await fetch(`${API_BASE_URL}/api/partner-shops${nextQuery ? `?${nextQuery}` : ''}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    const shops = Array.isArray(data?.shops) ? data.shops : [];
    shops.referralLock = data?.referral_lock || null;
    return shops;
}

// Repair shops from Google Places near a location, normalized into the shape the shop
// cards read. Photo URLs come back relative because they point at our own proxy, so they
// are absolutized here rather than in the component. `coords` ({ latitude, longitude },
// from "Use my current location") makes the driver's position the centre instead of the
// ZIP; it is sent for this one search only.
export async function validateShopSearchLocation({ location = '', coords = null, signal } = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shops/validate-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location, latitude: coords?.latitude, longitude: coords?.longitude }),
        signal,
    });
    return readJsonOrError(response);
}

export async function searchNearbyShops({ location, issueSummary = '', vehicleMake = '', diagnosticTicketId = '', radiusMiles = '', includeOutsideRadius = false, coords = null, signal } = {}) {
    const hasCoords = Number.isFinite(coords?.latitude) && Number.isFinite(coords?.longitude);
    const response = await fetch(`${API_BASE_URL}/api/shops/search`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            location,
            issue_summary: issueSummary || undefined,
            vehicle_make: vehicleMake || undefined,
            diagnostic_ticket_id: diagnosticTicketId || undefined,
            radius_miles: radiusMiles || undefined,
            include_outside_radius: includeOutsideRadius || undefined,
            latitude: hasCoords ? coords.latitude : undefined,
            longitude: hasCoords ? coords.longitude : undefined,
        }),
        signal,
    });
    const data = await readJsonOrError(response);
    const shops = (Array.isArray(data?.shops) ? data.shops : []).map((place) => ({
        id: `google-${place.place_id}`,
        place_id: place.place_id || '',
        partner_shop_id: place.partner_shop_id || null,
        is_mobile_mechanic: Boolean(place.is_mobile_mechanic),
        shop_type: place.partner_shop_id ? 'partner' : 'nearby',
        name: place.name || 'Repair shop',
        address: place.address || '',
        phone: place.phone || '',
        website: place.website || '',
        rating: place.rating ?? null,
        review_count: place.reviewCount ?? null,
        distance_miles: place.distanceMiles ?? null,
        distance_label: place.distanceLabel || '',
        outside_radius: Boolean(place.outsideRadius),
        open_now: typeof place.openNow === 'boolean' ? place.openNow : null,
        weekday_hours: Array.isArray(place.weekdayHours) ? place.weekdayHours : [],
        photo_url: place.photoUrl ? `${API_BASE_URL}${place.photoUrl}` : '',
        maps_url: place.mapsUrl || '',
        tags: place.primaryType ? [place.primaryType] : [],
        // #697: how well this shop fits the diagnosed repair, with the evidence behind it.
        match: place.match || null,
    }));
    shops.referralLock = data?.referral_lock || null;
    shops.hasMoreOutsideRadius = Boolean(data?.has_more_outside_radius);
    return shops;
}

export async function createShopReferral(payload, options = {}) {
    const query = options.partnerShopId
        ? `?${new URLSearchParams({ partner_shop_id: String(options.partnerShopId) }).toString()}`
        : '';
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals${query}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, app_origin: window.location.origin }),
    });
    return readJson(response);
}

export async function getReusableShopReferralLink(options = {}) {
    const params = new URLSearchParams();
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    if (typeof window !== 'undefined' && window.location?.origin) params.set('app_origin', window.location.origin);
    const query = params.toString();
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals/general-link${query ? `?${query}` : ''}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return { url: data?.url || '', code: data?.code || '' };
}

export async function createShopDirectJob(payload, options = {}) {
    const query = options.partnerShopId
        ? `?${new URLSearchParams({ partner_shop_id: String(options.partnerShopId) }).toString()}`
        : '';
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/direct${query}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function listShopReferrals(options = {}) {
    const params = new URLSearchParams();
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    if (typeof window !== 'undefined' && window.location?.origin) params.set('app_origin', window.location.origin);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals${query}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return Array.isArray(data?.referrals) ? data.referrals : [];
}

export async function getShopReferralLink(referralId, options = {}) {
    const params = new URLSearchParams();
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    if (typeof window !== 'undefined' && window.location?.origin) params.set('app_origin', window.location.origin);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals/${encodeURIComponent(referralId)}/link${query}`, {
        method: 'POST',
        credentials: 'include',
    });
    return readJson(response);
}

// Re-sends the referral link to the customer. `channels` is any of 'sms' / 'email';
// the response's `delivery` has the same per-channel shape as createShopReferral's.
export async function sendShopReferralLink(referralId, channels = ['sms', 'email'], options = {}) {
    const params = new URLSearchParams();
    if (options.partnerShopId) params.set('partner_shop_id', String(options.partnerShopId));
    if (typeof window !== 'undefined' && window.location?.origin) params.set('app_origin', window.location.origin);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals/${encodeURIComponent(referralId)}/send-link${query}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channels }),
    });
    return readJson(response);
}

export async function releaseShopReferral(referralId, reason = '') {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/referrals/${encodeURIComponent(referralId)}/release`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
    });
    return readJson(response);
}

export async function getShopReferral(token) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/${encodeURIComponent(token)}`);
    const data = await readJson(response);
    return data?.referral || null;
}

export async function getShopReferralBySlug(shopSlug) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/shops/${encodeURIComponent(shopSlug)}`);
    const data = await readJson(response);
    return data?.referral || null;
}

export async function getShopReferralByCode(code) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/codes/${encodeURIComponent(code)}`);
    const data = await readJson(response);
    return data?.referral || null;
}

export async function redeemShopReferral(token) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/${encodeURIComponent(token)}/redeem`, {
        method: 'POST',
        credentials: 'include',
    });
    return readJson(response);
}

export async function requestShopReferralOtp(token, channel) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/${encodeURIComponent(token)}/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel }),
    });
    return readJson(response);
}

export async function verifyShopReferralOtp(token, { channel, otp, webId = '' } = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/${encodeURIComponent(token)}/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, otp, web_id: webId }),
    });
    return readJson(response);
}

export async function redeemShopReferralBySlug(shopSlug, payload = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/shops/${encodeURIComponent(shopSlug)}/redeem`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return readJson(response);
}

export async function redeemShopReferralByCode(code, payload = {}) {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/codes/${encodeURIComponent(code)}/redeem`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return readJson(response);
}

export async function consentShopReferralTicket(diagnosticTicketId, consentText = '') {
    const response = await fetch(`${API_BASE_URL}/api/shop-referrals/tickets/${encodeURIComponent(diagnosticTicketId)}/consent`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent_confirmed: true, consent_text: consentText }),
    });
    return readJson(response);
}

export async function listPartnerRepairRequests(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.partner_shop_id) searchParams.set('partner_shop_id', String(params.partner_shop_id));
    if (params.diagnostic_ticket_id) searchParams.set('diagnostic_ticket_id', String(params.diagnostic_ticket_id));
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests${query ? `?${query}` : ''}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return Array.isArray(data?.requests) ? data.requests : [];
}

export async function getPartnerRepairRequest(requestId) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function listPartnerAdminRepairRequests(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.partner_shop_id) searchParams.set('partner_shop_id', String(params.partner_shop_id));
    if (params.status && params.status !== 'all') searchParams.set('status', String(params.status));
    if (params.q) searchParams.set('q', String(params.q));
    if (params.estimate_state && params.estimate_state !== 'all') {
        searchParams.set('estimate_state', String(params.estimate_state));
    }
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests${query ? `?${query}` : ''}`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return {
        requests: Array.isArray(data?.requests) ? data.requests : [],
        shops: Array.isArray(data?.shops) ? data.shops : [],
        demoAdminEnabled: Boolean(data?.demo_admin_enabled),
    };
}

export async function listShopRepairRequests(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.status && params.status !== 'all') searchParams.set('status', String(params.status));
    if (params.q) searchParams.set('q', String(params.q));
    if (params.estimate_state && params.estimate_state !== 'all') {
        searchParams.set('estimate_state', String(params.estimate_state));
    }
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests${query ? `?${query}` : ''}`, {
        credentials: 'include',
    });
    const data = await readJsonOrError(response);
    return {
        requests: Array.isArray(data?.requests) ? data.requests : [],
        shops: Array.isArray(data?.shops) ? data.shops : [],
        demoAdminEnabled: Boolean(data?.demo_admin_enabled),
    };
}

export async function listShopDashboardRepairRequests(params = {}) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests${partnerDashboardQuery(params)}`, {
        credentials: 'include',
    });
    const data = await readJsonOrError(response);
    return {
        requests: Array.isArray(data?.requests) ? data.requests : [],
        shops: Array.isArray(data?.shops) ? data.shops : [],
        demoAdminEnabled: Boolean(data?.demo_admin_enabled),
    };
}

export async function getPartnerEstimateEmailPreference() {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/email-preference`, {
        credentials: 'include',
    });
    const data = await readJson(response);
    return { email: data?.email || '', optIn: data?.opt_in ?? null };
}

export async function savePartnerEstimateEmailPreference(email) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/email-preference`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    const data = await response.json().catch(() => ({}));
    if ([404, 405, 501].includes(response.status)) {
        return { email, optIn: true, savedRemotely: false };
    }
    if (!response.ok) {
        throw new Error(getApiErrorMessage(data));
    }
    return { email: data?.email || email, optIn: data?.opt_in ?? true, savedRemotely: true };
}

// Create (or reuse) a lightweight diagnostic ticket for a scheduled-maintenance item so the
// shop drawer can request a partner estimate. Maintenance reports have no ticket of their own.
export async function createMaintenanceEstimateTicket({ title, summary = '', vehicleId = null }) {
    const response = await fetch(`${API_BASE_URL}/api/diagnostic-tickets/maintenance-estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, summary, vehicle_id: vehicleId }),
    });
    const data = await readJson(response);
    return {
        diagnosticTicketId: data?.diagnostic_ticket_id || null,
        ticketNumber: data?.ticket_number || '',
    };
}

export async function createPartnerRepairRequest(payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function getPartnerShopAccess(token) {
    const response = await fetch(`${API_BASE_URL}/api/partner-relay/shop-access/${encodeURIComponent(token)}`);
    const data = await readJson(response);
    return data?.request || null;
}

export async function sendPartnerShopAccessMessage(token, message) {
    const response = await fetch(`${API_BASE_URL}/api/partner-relay/shop-access/${encodeURIComponent(token)}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function sendPartnerShopAccessEstimate(token, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-relay/shop-access/${encodeURIComponent(token)}/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function updatePartnerRepairRequestStatus(requestId, status, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function updatePartnerEstimateLineAuthorizations(requestId, decisions, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}/estimate-authorization`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions, notes }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

// Bookable slots for a shop over a date range. Each slot: { slot_start, slot_end,
// capacity, booked, remaining, available }. Full slots come back available:false.
export async function fetchShopAvailabilitySlots(shopId, fromDate, toDate) {
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const query = params.toString();
    const response = await fetch(
        `${API_BASE_URL}/api/shops/${encodeURIComponent(shopId)}/availability${query ? `?${query}` : ''}`,
        { credentials: 'include' },
    );
    return readJson(response);
}

// Uses readJsonOrError so a 409 (slot just taken) reaches the caller with error.status
// and error.data — the booking card needs that to say "pick another slot" and refresh.
export async function schedulePartnerRepairRequest(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}/schedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function schedulePartnerAdminRepairRequest(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/schedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

// Shop portal (shop-auth session) booking one of its own jobs from the calendar's
// New Appointment flow. Same capacity/closure/double-booking checks as every other path;
// surfaces status so a 409 (slot taken / closed) can prompt "pick another time".
export async function scheduleShopRepairRequest(requestId, payload) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/schedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function requestShopRepairRequestReschedule(requestId, payload = {}) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/reschedule-request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function requestPartnerAdminRepairRequestReschedule(requestId, payload = {}) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/reschedule-request`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function syncPartnerAdminAppointmentCalendar(requestId) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/calendar-sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function savePartnerAdminServiceNotes(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/service-notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function saveShopServiceNotes(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests/${encodeURIComponent(requestId)}/service-notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function saveShopDashboardServiceNotes(requestId, payload) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/service-notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function uploadPartnerAdminServiceNoteMedia(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/service-notes/media`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function deletePartnerAdminServiceNoteMedia(requestId, mediaId) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/service-notes/media/${encodeURIComponent(mediaId)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function uploadShopDashboardServiceNoteMedia(requestId, payload) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/service-notes/media`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function deleteShopDashboardServiceNoteMedia(requestId, mediaId) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/service-notes/media/${encodeURIComponent(mediaId)}`, {
        method: 'DELETE',
        credentials: 'include',
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function sendPartnerRepairRequestMessage(requestId, message) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function updatePartnerAdminRepairRequestStatus(requestId, status, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function updateShopRepairRequestStatus(requestId, status, notes = '') {
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function updateShopDashboardRepairRequestStatus(requestId, status, notes = '') {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, notes }),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function sendPartnerAdminRepairRequestMessage(requestId, message) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function sendShopRepairRequestMessage(requestId, message) {
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests/${encodeURIComponent(requestId)}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function sendShopDashboardRepairRequestMessage(requestId, message) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function sendPartnerEstimate(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-repair-requests/${encodeURIComponent(requestId)}/estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function sendPartnerAdminEstimate(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJson(response);
    return data?.request || null;
}

export async function sendShopEstimate(requestId, payload) {
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests/${encodeURIComponent(requestId)}/estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

export async function sendShopDashboardEstimate(requestId, payload) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await readJsonOrError(response);
    return data?.request || null;
}

// Ask the backend to LLM-suggest estimate line items from the customer's request +
// diagnostic report. Returns { lines: [{line_type, description, amount}], note } on success,
// or null so the caller can fall back to the static presets.
export async function suggestPartnerAdminEstimate(requestId) {
    const response = await fetch(`${API_BASE_URL}/api/partner-admin/repair-requests/${encodeURIComponent(requestId)}/suggest-estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await readJson(response);
    if (!data || data.status !== 'success' || !Array.isArray(data.lines) || data.lines.length === 0) {
        return null;
    }
    return { lines: data.lines, note: data.note || '' };
}

export async function suggestShopEstimate(requestId) {
    const response = await fetch(`${API_BASE_URL}/api/shop/repair-requests/${encodeURIComponent(requestId)}/suggest-estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await readJsonOrError(response);
    if (!data || data.status !== 'success' || !Array.isArray(data.lines) || data.lines.length === 0) {
        return null;
    }
    return { lines: data.lines, note: data.note || '' };
}

export async function suggestShopDashboardEstimate(requestId) {
    const response = await fetch(`${PARTNER_DASHBOARD_BASE}/repair-requests/${encodeURIComponent(requestId)}/suggest-estimate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    });
    const data = await readJsonOrError(response);
    if (!data || data.status !== 'success' || !Array.isArray(data.lines) || data.lines.length === 0) {
        return null;
    }
    return { lines: data.lines, note: data.note || '' };
}

export function formatPartnerMoney(value, currency = 'USD') {
    const amount = Number(value || 0);
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        }).format(amount);
    } catch (_) {
        return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
    }
}

export function getPartnerLifecycleStatus(status) {
    const normalized = String(status || '').toLowerCase();
    if (['submitted', 'viewed', 'accepted'].includes(normalized)) return 'submitted';
    if (['estimate_sent', 'estimate_accepted'].includes(normalized)) return 'estimate_sent';
    if (['scheduled', 'in_service', 'ready_for_pickup'].includes(normalized)) return normalized;
    if (['picked_up', 'completed'].includes(normalized)) return 'completed';
    if (['declined', 'failed', 'cancelled'].includes(normalized)) return normalized;
    return 'submitted';
}

export function getPartnerStatusLabel(status, perspective = 'driver') {
    const lifecycleStatus = getPartnerLifecycleStatus(status);
    const shopLabels = {
        submitted: 'Request Received',
        estimate_sent: 'Awaiting Approval',
        scheduled: 'Scheduled',
        in_service: 'In Service',
        ready_for_pickup: 'Ready for Pickup',
        completed: 'Completed',
        declined: 'Declined',
        failed: 'Failed',
        cancelled: 'Cancelled',
    };
    const driverLabels = {
        submitted: 'Estimate Requested',
        estimate_sent: 'Estimate Received',
        scheduled: 'Scheduled',
        in_service: 'In Service',
        ready_for_pickup: 'Ready for Pickup',
        completed: 'Completed',
        declined: 'Declined',
        failed: 'Failed',
        cancelled: 'Cancelled',
    };
    const labels = perspective === 'shop' ? shopLabels : driverLabels;
    return labels[lifecycleStatus] || labels.submitted;
}

export function formatPartnerScheduledAppointment(request, fallbackTimezone = 'America/Denver') {
    if (!request?.scheduled_start_at) return '';
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: request.scheduled_timezone || fallbackTimezone,
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(new Date(request.scheduled_start_at));
    } catch (_) {
        return new Date(request.scheduled_start_at).toLocaleString();
    }
}

export function getPartnerPreferredAppointment(request = {}) {
    const summary = request?.shop_ready_summary || {};
    const raw = String(
        summary.preferredAppointment ||
        summary.referralAppointment ||
        request?.external_payload?.referral?.appointment_date ||
        ''
    ).trim();
    if (!raw) return null;

    const normalized = raw
        .replace(/\s+at\s+/i, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const parsed = new Date(normalized);
    if (!Number.isFinite(parsed.getTime())) {
        return { raw, date: '', time: '', label: raw };
    }
    const date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
    const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
    return { raw, date, time, label: raw };
}

function compactCalendarText(value, max = 1200) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function formatGoogleCalendarDate(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function getPartnerCalendarVehicleLabel(request = {}) {
    const summary = request?.shop_ready_summary || {};
    const vehicle = request?.vehicle || {};
    const vehicleName = compactCalendarText(summary.vehicleName);
    if (vehicleName) return vehicleName;
    const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
        .map((part) => compactCalendarText(part))
        .filter(Boolean);
    return parts.length ? parts.join(' ') : 'Vehicle';
}

function getPartnerCalendarTitle(request = {}) {
    const serviceTitle = compactCalendarText(request?.services?.[0]?.title || request?.shop_ready_summary?.customerComplaint || 'Repair appointment', 140);
    return compactCalendarText(`Dash appointment: ${getPartnerCalendarVehicleLabel(request)} - ${serviceTitle}`, 180);
}

export function buildPartnerGoogleCalendarUrl(request = {}) {
    if (!request?.scheduled_start_at) return '';
    const start = new Date(request.scheduled_start_at);
    if (!Number.isFinite(start.getTime())) return '';
    const storedEnd = request.scheduled_end_at ? new Date(request.scheduled_end_at) : null;
    const end = storedEnd && Number.isFinite(storedEnd.getTime())
        ? storedEnd
        : new Date(start.getTime() + 15 * 60 * 1000);
    const dates = `${formatGoogleCalendarDate(start)}/${formatGoogleCalendarDate(end)}`;
    const shop = request.partner_shop || {};
    const summary = request.shop_ready_summary || {};
    const estimate = request.latest_estimate || {};
    const details = [
        'Dash repair appointment',
        request.ticket_number ? `Ticket: ${request.ticket_number}` : '',
        shop.name ? `Shop: ${shop.name}` : '',
        `Vehicle: ${getPartnerCalendarVehicleLabel(request)}`,
        summary.customerComplaint ? `Issue: ${compactCalendarText(summary.customerComplaint, 240)}` : '',
        estimate.total_amount ? `Estimate: ${formatPartnerMoney(estimate.total_amount, estimate.currency)}` : '',
        request.schedule_notes ? `Notes: ${compactCalendarText(request.schedule_notes, 400)}` : '',
    ].filter(Boolean).join('\n');
    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: getPartnerCalendarTitle(request),
        dates,
        details,
        location: compactCalendarText(shop.address || shop.name || '', 300),
        ctz: request.scheduled_timezone || shop.timezone || 'America/Denver',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
